/**
 * Shared IPC types for renderer process
 * Safe for import in both main and renderer processes
 */

import {
  LbNotificationCommands,
  LbNotificationEvents,
} from '../shared/lb-notification.types.ts';

import { AppManagerCommands } from './app.types.ts';
import { HummingbirdEvents } from './hummingbird.types.ts';
import { MainWindowCommands } from './window.types.ts';

export enum IPCEventType {
  // Shared events
  ping = 'ping',
  acknowledge = 'acknowledge',
  contextKitDidCrash = 'contextkit-did-crash',
  contextKitDidRecover = 'contextkit-did-recover',

  // Renderer events (from Electron main to the renderer)
  externalLinkRequested = 'external-link-requested',

  // Inbound events (from ContextKit to Electron)
  observerStatus = 'is-observer-running',
  traversedAppContent = 'traversed-app-content',
  meetingAppsUpdated = 'meeting-apps-updated',
  meetingParticipantsUpdated = 'meeting-participants-updated',
  meetingSpeakerNameResolved = 'meeting-speaker-name-resolved',
  meetingStarted = 'meeting-started',
  meetingStopped = 'meeting-stopped',
  meetingTranscriptDelta = 'meeting-transcript-delta',
  meetingLocalTranscriptionReadinessChanged = 'meeting-local-transcription-readiness-changed',
  meetingTranscriptionInputDeviceChanged = 'meeting-transcription-input-device-changed',
  meetingPostProcessingStateChanged = 'meeting-post-processing-state-changed',
  meetingOpenChat = 'meeting-open-chat',
  meetingMainWindowFocusChange = 'meeting-main-window-focus-change',
  meetingStatusWindowMoved = 'meeting-status-window-moved',
  meetingStatusWindowShouldCollapse = 'meeting-status-window-should-collapse',
  meetingRequestQuickNote = 'meeting-request-quick-note',
  meetingRequestPrepCalendarEvent = 'meeting-request-prep-calendar-event',
  meetingRequestStartCalendarEvent = 'meeting-request-start-calendar-event',
  meetingRequestStartNote = 'meeting-request-start-note',
  meetingRequestStopNote = 'meeting-request-stop-note',
  meetingMenubarCalendarConfigUpdated = 'meeting-menubar-calendar-config-updated',
  meetingMenubarCalendarDataUpdated = 'meeting-menubar-calendar-data-updated',
  meetingSegmentEvent = 'meeting-segment-event',
  meetingDebugEvent = 'meeting-debug-event',
  meetingStateDidChange = 'meeting-state-did-change',
  meetingInterrupt = 'meeting-interrupt',
  meetingStartInputSilent = 'meeting-start-input-silent',
  quitBlockedByMeeting = 'quit-blocked-by-meeting',
  systemEventReceived = 'system-event-received',
  meetingAudioEnergyLevelsReceived = 'audio-energy-levels-received',
  spriteUpdated = 'sprite-updated',
  apiSenderEventReceived = 'api-sender-event-received',
  exclusionUpdated = 'exclusion-updated',
  requestMainWindowPresenting = 'request-main-window-presenting',
  requestOpenSettings = 'request-open-settings',
  requestAppQuiting = 'request-app-quiting',
  requestAuthToken = 'request-auth-token',
  pauseContextCollection = 'pause-context-collection',
  disableContextCollection = 'disable-context-collection',
  enableContextCollection = 'enable-context-collection',
  installAppUpdate = 'install-app-update',
  frontAppContext = 'front-app-context',
  resourcesSnapshotReceived = 'resources-snapshot-received',
  excludeApp = 'exclude-app',
  excludeDomain = 'exclude-domain',
  includeApp = 'include-app',
  includeDomain = 'include-domain',
  readabilityIssue = 'readability-issue',
  debugSnapshotReport = 'debug-snapshot-report',
  lbNotificationsQueueNotification = LbNotificationCommands.QueueNotification,
  lbNotificationsSendNativeNotification = LbNotificationCommands.SendNativeNotification,
  lbNotificationsClearAll = LbNotificationCommands.ClearAll,
  lbNotificationsQueueCount = LbNotificationCommands.QueueCount,
  lbNotificationsHasNotification = LbNotificationCommands.HasNotification,
  lbNotificationsRemoveQueuedById = LbNotificationCommands.RemoveQueuedById,
  lbNotificationsDismissById = LbNotificationCommands.DismissById,
  lbNotificationsGetVolume = LbNotificationCommands.GetVolume,
  lbNotificationsSetVolume = LbNotificationCommands.SetVolume,
  lbNotificationsGetEnabled = LbNotificationCommands.GetEnabled,
  lbNotificationsSetEnabled = LbNotificationCommands.SetEnabled,
  lbNotificationsSetSoundChoice = LbNotificationCommands.SetSoundChoice,
  lbNotificationsPreviewSoundChoice = LbNotificationCommands.PreviewSoundChoice,
  lbNotificationsWillNotify = LbNotificationEvents.WillNotify,
  lbNotificationsDidPresent = LbNotificationEvents.DidPresent,
  lbNotificationsDidNotify = LbNotificationEvents.DidNotify,
  lbNotificationsQueueChanged = LbNotificationEvents.QueueChanged,
  lbNotificationsAction = LbNotificationCommands.NotificationAction,
  lbNotificationsInteractionChanged = LbNotificationCommands.InteractionChanged,
  lbNotificationsRequestDismissNotification = LbNotificationCommands.RequestDismissNotification,
  mainWindowNavigateToRoute = MainWindowCommands.NavigateToRoute,
  mainWindowEnterFullScreen = MainWindowCommands.EnterFullScreen,
  mainWindowLeaveFullScreen = MainWindowCommands.LeaveFullScreen,
  getAppVisibilityMode = AppManagerCommands.GetAppVisibilityMode,
  setAppVisibilityMode = AppManagerCommands.SetAppVisibilityMode,
  hummingbirdBecomeKeyWindow = HummingbirdEvents.BecomeKeyWindow,
  hummingbirdOpenThread = HummingbirdEvents.OpenThread,
  modifierTapDetected = 'modifier-tap-detected',
  modifierHoldStarted = 'modifier-hold-started',
  modifierHoldReleased = 'modifier-hold-released',
  dictationTranscriptDelta = 'dictation-transcript-delta',
  dictationSessionStateChanged = 'dictation-session-state-changed',
  dictationAudioLevel = 'dictation-audio-level',
  meetingIntentOverlayShow = 'meeting-intent-overlay-show',
  meetingIntentOverlayReady = 'meeting-intent-overlay-ready',
  meetingIntentOverlayWillShow = 'meeting-intent-overlay-will-show',
  meetingIntentOverlayPresented = 'meeting-intent-overlay-presented',
  meetingIntentOverlayResolve = 'meeting-intent-overlay-resolve',

  // Outbound events (from Electron to observer lib)
  isObserverRunning = 'is-observer-running',
  configureModifierTapMonitor = 'configure-modifier-tap-monitor',
  startModifierTapMonitor = 'start-modifier-tap-monitor',
  stopModifierTapMonitor = 'stop-modifier-tap-monitor',
  updateContentFilters = 'update-content-filters',
  syncExclusions = 'sync-exclusions',
  setMeetingDetectionDisabledApps = 'set-meeting-detection-disabled-apps',
  setMeetingDetectionDisabledSites = 'set-meeting-detection-disabled-sites',
  runObserver = 'run-observer',
  stopObserver = 'stop-observer',
  setTokenAndUrl = 'set-token-and-url',
  setUserEmail = 'set-user-email',
  setFeatureFlags = 'set-feature-flags',
  accessibilityPermissionsStatus = 'accessibility-permissions-status',
  requestAccessibilityPermissions = 'request-accessibility-permissions',
  quitObserverProcess = 'quit-observer-process',
  startNoteTaking = 'start-note-taking',
  stopNoteTaking = 'stop-note-taking',
  traverseApp = 'traverse-app',
  authTokensExpired = 'auth-tokens-expired',
  clearAuth = 'clear-auth',
  updateStatusChanged = 'update-status-changed',
  manualSnapshot = 'manual-snapshot',
  pasteText = 'paste-text',
  startDictation = 'start-dictation',
  stopDictation = 'stop-dictation',
  deleteContext = 'delete-context',
  resourcesSnapshot = 'resources-snapshot',
  getMentionableWindows = 'get-mentionable-windows',
  getCapturableWindows = 'get-capturable-windows',
  getFrontAppContext = 'get-front-app-context',
  mentionableWindowsReceived = 'mentionable-windows-received',
  capturableWindowsReceived = 'capturable-windows-received',
  captureAppScreenshot = 'capture-app-screenshot',
  appScreenshotCaptured = 'app-screenshot-captured',
  meetingHeartbeat = 'meeting-heartbeat',
  meetingSpeakerSightings = 'meeting-speaker-sightings',
  meetingLocalTranscriptionReadinessGet = 'meeting-local-transcription-readiness-get',
  meetingLocalTranscriptionReadinessRetry = 'meeting-local-transcription-readiness-retry',
  meetingGetTranscriptionInputDevices = 'meeting-get-transcription-input-devices',
  meetingSetTranscriptionInputDevice = 'meeting-set-transcription-input-device',
  meetingPostProcessingStart = 'meeting-post-processing-start',
  meetingPostProcessingRecoverableRecordings = 'meeting-post-processing-recoverable-recordings',
  meetingPostProcessingAcknowledgeResult = 'meeting-post-processing-acknowledge-result',
  meetingPostProcessingSyncState = 'meeting-post-processing-sync-state',
  setSubscriptionPlan = 'set-subscription-plan',
  setBackendChoice = 'set-backend-choice',
  startEventKitCoordinator = 'start-eventkit-coordinator',
  stopEventKitCoordinator = 'stop-eventkit-coordinator',
  restartEventKitCoordinator = 'restart-eventkit-coordinator',
  getEventKitReminderLists = 'get-eventkit-reminder-lists',
  startContactsCoordinator = 'start-contacts-coordinator',
  stopContactsCoordinator = 'stop-contacts-coordinator',
  restartContactsCoordinator = 'restart-contacts-coordinator',
  getSnapshotDumpApps = 'get-snapshot-dump-apps',
  snapshotDumpAppsReceived = 'snapshot-dump-apps-received',
  getSnapshotDumpArchives = 'get-snapshot-dump-archives',
  snapshotDumpArchivesReceived = 'snapshot-dump-archives-received',
  enableDebugStream = 'enable-debug-stream',
  disableDebugStream = 'disable-debug-stream',
  presentAccessibilityPermissionAssistant = 'present-accessibility-permission-assistant',
  dismissAccessibilityPermissionAssistant = 'dismiss-accessibility-permission-assistant',
  invokePlatformAction = 'invoke-platform-action',
}

/** Mirrors `ModifierTapConsumer` in ContextKit; values must stay identical. */
export const MODIFIER_TAP_CONSUMERS = [
  'hummingbird',
  'hummingbirdVoice',
  'dictation',
  'dictationHold',
] as const;

export type ModifierTapConsumer = (typeof MODIFIER_TAP_CONSUMERS)[number];

/** `fn` is native-only: `globalShortcut` drops it and registers the bare key. */
export const MODIFIER_TAP_KEYS = [
  'option',
  'command',
  'control',
  'shift',
  'fn',
] as const;

/** `+`-joined modifiers, so the wire carries a combo rather than one key. */
export const isModifierCombo = (value: string): boolean => {
  const tokens = value.split('+');
  return (
    tokens.length > 0 &&
    tokens.every((token) =>
      (MODIFIER_TAP_KEYS as readonly string[]).includes(token)
    )
  );
};
